import { addDays, endOfDay, isAfter, isBefore, startOfDay, startOfWeek } from 'date-fns';
import { UTCDate } from '@date-fns/utc';
import type { SimpleDate } from '../types';
import { addDaysSD } from '../dates';

/*
  Which board dates a player may open, as far as the calendar alone can say.

  Deliberately stops short of the subscription: the caller knows whether this player has one, and on
  the website that answer costs a Stripe round trip worth avoiding for the majority of requests that
  never need it. What's here is the part that is pure arithmetic on dates — and the part most easily
  got subtly wrong, which is why it has one definition rather than one per client.

  The server remains the authority regardless; this decides what a calendar draws, not what a player
  is served.
*/

const HOUR = 60 * 60 * 1000;

/** Number of days into the future influencers can access puzzles. */
export const INFLUENCER_FUTURE_DAYS = 7;

export type BoardDateWindow =
  /** In the open window: the current week, plus the Monday grace below. */
  | 'open'
  /** Earlier than the open window. Free to read only with a subscription. */
  | 'past'
  /** Not released yet. */
  | 'future';

export interface BoardDateWindowOptions {
  /** The current instant; injectable so this can be reasoned about and tested. */
  now?: Date;
  /**
   * How many days ahead this player may see. One for everyone — a board is readable the day before
   * it lands — and more for influencers, who preview further.
   */
  futureDays?: number;
}

/**
 * Where a date sits relative to what's currently open.
 *
 * Uses UTC throughout so the answer doesn't depend on the host's timezone, and widens the week
 * backwards for the first 24 hours of it: a player in a western timezone still calls it Sunday when
 * UTC has rolled over to Monday, and would otherwise lose last week's puzzles a day early.
 */
export function boardDateWindow(date: SimpleDate, { now = new Date(), futureDays = 1 }: BoardDateWindowOptions = {}) {
  const today = startOfDay(new UTCDate(now));
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  const hoursSinceWeekStart = (today.getTime() - weekStart.getTime()) / HOUR;
  const openFrom = hoursSinceWeekStart < 24 ? new UTCDate(weekStart.getTime() - 7 * 24 * HOUR) : weekStart;

  const requested = new UTCDate(date);
  if (isAfter(requested, endOfDay(addDays(today, futureDays)))) return 'future' as BoardDateWindow;
  if (isBefore(requested, openFrom)) return 'past' as BoardDateWindow;
  return 'open' as BoardDateWindow;
}

/**
 * Whether a board's puzzle data may still be rewritten by an admin editor. A board is out of reach
 * once it's released or sitting in the influencer preview window, and free to change while it's
 * unscheduled (`null`) or dated beyond it. Editing a puzzle invalidates plays in progress, so the
 * editors check this on the server, not just in the UI that hides their links. Metadata (name,
 * slug, …) stays editable either way.
 */
export function isPuzzleEditable(date: string | null | undefined, today: SimpleDate): boolean {
  if (!date) return true;
  return date > addDaysSD(today, INFLUENCER_FUTURE_DAYS);
}
